/**
 * Pure data mapping for the v4 pages (unit-tested in model.test.ts). Nothing here fetches, signs or invents a value:
 * every function derives display facts from API/demo snapshots, or recomputes a published rule (waterfall split,
 * base identity renderer) with the reference implementation so the page can show whether they agree.
 */
import { baseTraits, waterfallSplit, type WaterfallPolicy } from '@runner/simulation';
import { fixtureCollectionManifest, validateCollectionManifest, type ManifestValidationResult } from '@runner/schema';
import type { Hex } from 'viem';
import type {
  EconomicProfileView,
  FundingView,
  IdentitySummary,
  OverheatView,
  Phase,
  PolicyView,
  RewardsAccount,
  RewardsBreakdown,
  RoadView,
  RunView,
  SourceMode,
  Stage,
  TreasurySnapshot,
  WorldView,
} from './types';

export const big = (v: string | number | bigint | null | undefined): bigint => {
  if (v === null || v === undefined || v === '') return 0n;
  return typeof v === 'bigint' ? v : BigInt(v);
};

// ------------------------------------------------------------------------------------------------
// Phases
// ------------------------------------------------------------------------------------------------

export const PHASE_LABEL: Record<Phase, string> = {
  'warm-up': 'Warm-up',
  funding: 'Funding',
  preview: 'Course preview',
  running: 'Running',
  finished: 'Result',
  replay: 'Replay',
};

const FUNDING_REASON: Record<string, string> = {
  fundable: 'The next run can start now.',
  'no-active-config': 'No configuration is active yet.',
  'funding-paused': 'New run funding is paused by the guardian (claims, withdrawals and transfers are unaffected).',
  'road-reserve-below-minimum': 'Trading fees are funding the next run: every buy or sell adds to it.',
  'operations-below-provision': 'Trading fees are funding the next run: every buy or sell adds to it.',
  'not-fundable': 'The next run can’t start yet.',
};

/** How far trading fees have funded the next run, while warming up for lack of fees: the slower of the two things a
 *  run needs (the operations provision and the road minimum), counting the open period's estimated split, and the
 *  trading volume (in quote units) still needed at the hook fee. Fees from closed periods count once the keeper
 *  settles them. Null when a run is in flight, a run is fundable, or the data is missing. */
export function nextRunFunding(view: WorldView): { pct: number; volumeToGo: number } | null {
  const f = view.funding;
  const br = view.baseRewards;
  if (view.phase !== 'warm-up' || !f || f.fundable || f.fundingPaused || !br) return null;
  if (f.reason !== 'operations-below-provision' && f.reason !== 'road-reserve-below-minimum') return null;
  const est = br.openPeriodSplitEstimate;
  const opsHave = Number(f.operations) + Number(est.operations);
  const roadHave = Number(f.roadReserve) + Number(est.road);
  const opsNeed = Number(f.opsProvision);
  const roadNeed = Number(f.minRunBudget);
  const part = (have: number, need: number) => (need > 0 ? Math.min(1, have / need) : 1);
  const pct = Math.floor(Math.min(part(opsHave, opsNeed), part(roadHave, roadNeed)) * 100);
  const fee = view.market.hookFeeBps / 10_000;
  const toGo = (need: number, have: number, bps: number) => (bps > 0 ? Math.max(0, need - have) / (bps / 10_000) : 0);
  const feesToGo = Math.max(toGo(opsNeed, opsHave, br.policy.opsBps), toGo(roadNeed, roadHave, br.policy.roadBps));
  const volumeToGo = fee > 0 ? feesToGo / fee / 10 ** view.quote.decimals : 0;
  return { pct, volumeToGo };
}

/** D48: how a run with budget 0 (started unfunded under a configuration with no minimum budget) is always described. */
export const EXHIBITION_LABEL = 'Exhibition run — no road budget at stake';

/** Short label for the phase pill: the phase, refined by the stage where it matters to a player. */
export function phaseLabel(phase: Phase, stage: Stage, exhibition = false): string {
  if (phase === 'running') return stage === 'abandonable' ? 'Past stall deadline' : stage === 'catch-up' ? 'Resolving steps' : 'Steering open';
  if (phase === 'funding') {
    const head = exhibition ? 'Exhibition' : 'Funded';
    return stage === 'randomness-ready' ? `${head} · starting` : stage === 'abandonable' ? `${head} · randomness overdue` : `${head} · awaiting randomness`;
  }
  return PHASE_LABEL[phase];
}

/**
 * One sentence explaining the phase, from protocol facts only. `exhibition`: the run in flight is an exhibition run
 * (budget 0, D48) — it is never described as funded.
 */
export function phaseExplanation(phase: Phase, stage: Stage, funding: FundingView | null, replayRunId: number | null, exhibition = false): string {
  switch (phase) {
    case 'warm-up': {
      if (funding?.fundable) return `No run right now. The next one can start any moment${replayRunId ? `; run ${replayRunId} is still available as a replay` : ''}.`;
      if (funding?.exhibition) return `No run in flight. The road reserve cannot fund the next run, so it would be an exhibition run — no road budget at stake; anyone may request its course${replayRunId ? `; the replay of run ${replayRunId} stays available` : ''}.`;
      const why = FUNDING_REASON[funding?.reason ?? 'not-fundable'] ?? FUNDING_REASON['not-fundable'];
      return `No run right now. ${why} Replays stay available${replayRunId ? ` (last: run ${replayRunId})` : ''}; nothing is paid out while warming up.`;
    }
    case 'funding':
      if (exhibition) {
        return stage === 'abandonable'
          ? `${EXHIBITION_LABEL}. Its course randomness never arrived; anyone may abandon it (nothing returns: it reserved no budget).`
          : `${EXHIBITION_LABEL}: the road reserve could not fund it, so it plays without moving any road money. The course appears when the randomness provider answers.`;
      }
      return stage === 'abandonable'
        ? 'The course never arrived, so anyone can cancel this run; all its money goes back to the reserve.'
        : 'Money for this run is set aside. The course appears in a moment.';
    case 'preview':
      return 'The course is set. Steering opens in a moment.';
    case 'running':
      if (stage === 'abandonable') {
        return 'This run stalled, so anyone can finish or cancel it. Money already paid out stays; the rest goes back to the reserve.';
      }
      return stage === 'catch-up'
        ? 'Catching up on steps that are already decided.'
        : 'Trades right now decide RUNNER’s next step.';
    case 'finished':
      return 'The run is over. Its results are final, and you can replay it.';
    case 'replay':
      return funding && !funding.fundable && funding.exhibition
        ? `Between runs. The next run would be an exhibition run — no road budget at stake; the last run is available as a verified replay.`
        : 'Between runs. Watch the last one as a verified replay.';
  }
}

// ------------------------------------------------------------------------------------------------
// What this run decides (road budget) — never mixed with base rewards
// ------------------------------------------------------------------------------------------------

export interface RunDecides {
  funded: boolean;
  /** Exhibition run (D48): an escrow opened with budget 0 — the run plays, no road budget is at stake. */
  exhibition: boolean;
  budget: bigint;
  assigned: bigint;
  unassigned: bigint;
  toHolders: bigint;
  toForge: bigint;
  /** After close: returned to the road reserve (carry-forward). Null while open. */
  returned: bigint | null;
  open: boolean;
  waterSlice: bigint;
  overheatPerStep: bigint;
  overheatCap: bigint;
  overheatSteps: number;
  bias: { active: boolean; stepsLeft: number; assigned: bigint; capLeft: bigint } | null;
  cohortEpoch: number | null;
  policyId: number | null;
  /** Sum check: assigned = toHolders + toForge and budget = assigned + unassigned (+ returned once closed). */
  consistent: boolean;
}

export function runDecides(road: RoadView | null, overheat: OverheatView | null): RunDecides | null {
  if (!road) return null;
  const e = road.escrow;
  const budget = big(e?.budget);
  const assigned = big(e?.assigned);
  const toHolders = big(e?.toHolders);
  const toForge = big(e?.toForge);
  const unassigned = big(e?.unassigned);
  const returned = e?.returnedToRoad === null || e?.returnedToRoad === undefined ? null : big(e.returnedToRoad);
  const open = !!e?.open;
  const consistent = assigned === toHolders + toForge && (open ? budget === assigned + unassigned : budget === assigned + (returned ?? 0n));
  const cap = big(road.params.overheatCap);
  return {
    funded: budget > 0n,
    // Every escrow ever opened carries a policy id >= 1; one with budget 0 is an exhibition run.
    exhibition: !!e && budget === 0n && e.policyId > 0,
    budget,
    assigned,
    unassigned,
    toHolders,
    toForge,
    returned,
    open,
    waterSlice: big(road.waterSlice),
    overheatPerStep: big(road.overheatPerStep),
    overheatCap: cap,
    overheatSteps: road.params.overheatBiasSteps,
    bias: overheat
      ? { active: overheat.active, stepsLeft: overheat.stepsLeft, assigned: big(overheat.assigned), capLeft: cap - big(overheat.assigned) }
      : null,
    cohortEpoch: e ? e.cohortEpoch : null,
    policyId: e ? e.policyId : null,
    consistent,
  };
}

/** Where the remaining unassigned budget would go at the finish, given the current route. */
export function exitPreview(route: number, inSplit: boolean): string {
  if (route === 2) return 'Heading Harvest: at the finish, the money left in this run goes to depositors.';
  if (route === 1) return 'Heading Forge: at the finish, the money left in this run goes to the Forge.';
  return inSplit ? 'At the fork.' : 'No fork taken yet: finishing down the middle splits what’s left half to depositors, half to the Forge.';
}

// ------------------------------------------------------------------------------------------------
// Rewards: one ledger, no double counting
// ------------------------------------------------------------------------------------------------

export interface PrincipalBreakdown {
  total: bigint;
  ordinary: bigint;
  identityBacked: bigint;
  eligible: bigint;
  pending: bigint;
  ordinaryEligible: bigint;
  ordinaryPending: bigint;
  identityEligible: bigint;
  identityPending: bigint;
  /** ordinary + identityBacked === total and eligible + pending === total (never added twice). */
  consistent: boolean;
}

export function principal(a: RewardsAccount | null): PrincipalBreakdown {
  const p = a?.principal;
  const total = big(p?.total);
  const ordinary = big(p?.ordinary);
  const identityBacked = big(p?.identityBacked);
  const eligible = big(p?.eligibleNow);
  const pending = big(p?.pendingNow);
  return {
    total,
    ordinary,
    identityBacked,
    eligible,
    pending,
    ordinaryEligible: big(p?.ordinaryEligible),
    ordinaryPending: big(p?.ordinaryPending),
    identityEligible: big(p?.identityEligible),
    identityPending: big(p?.identityPending),
    consistent: ordinary + identityBacked === total && eligible + pending === total,
  };
}

export interface ClaimSummary {
  direct: bigint;
  deferred: bigint;
  run: bigint;
  total: bigint;
  lockedDeferred: bigint;
  unpaid: bigint;
  prizeOwed: bigint;
  /** Anything owed to this account (claimable, still-locked deferred, unpaid). Claims stay with the account that
   *  earned them — also after it sold, burned or withdrew the principal that earned them. */
  hasClaims: boolean;
}

export function claims(a: RewardsAccount | null): ClaimSummary {
  const c = a?.claimable;
  const direct = big(c?.direct);
  const deferred = big(c?.deferred);
  const run = big(c?.run);
  const total = big(c?.total);
  const hasClaims = total > 0n || big(a?.lockedDeferredEstimate) > 0n || big(a?.unpaid) > 0n;
  return {
    direct,
    deferred,
    run,
    total,
    lockedDeferred: big(a?.lockedDeferredEstimate),
    unpaid: big(a?.unpaid),
    prizeOwed: big(a?.prizeOwed),
    hasClaims,
  };
}

// ------------------------------------------------------------------------------------------------
// Old claims: per-epoch and per-Runner entitlements (GET /v1/rewards/:account/breakdown)
// ------------------------------------------------------------------------------------------------

export const ENTITLEMENT_STATUS_LABEL: Record<RewardsBreakdown['epochs'][number]['streams'][number]['status'], string> = {
  claimed: 'claimed',
  claimable: 'claimable',
  locked: 'locked until release',
  'not-final': 'not final yet',
};

const ENDED_LABEL = { transferred: 'sold / transferred', redeemed: 'redeemed (burned)', converted: 'converted back' } as const;

export interface FormerRunnerRow {
  tokenId: string;
  /** How this account's holding ended (breakdown), or the token's status when only the runners list is known. */
  label: string;
  heldFrom: number | null;
  heldUntil: number | null;
  epochs: number[];
  earned: bigint | null;
  unclaimed: bigint | null;
}

export interface OldClaimsEpochRow {
  epoch: number;
  weight: bigint;
  identityWeight: bigint;
  runnerIds: string[];
  direct: { amount: bigint; status: string } | null;
  deferred: { amount: bigint; status: string } | null;
  run: { amount: bigint; status: string } | null;
  amount: bigint;
  unclaimed: bigint;
}

export interface OldClaimsView {
  /** Runners this account held before (breakdown first; the indexed runners list as a fallback). */
  formerRunners: FormerRunnerRow[];
  epochs: OldClaimsEpochRow[];
  epochCount: number;
  /** null while the breakdown is not loaded. */
  complete: boolean | null;
  reason: string | null;
  /** Σ unclaimed over the listed breakdown (claimable + locked + not final). */
  unclaimed: bigint;
}

/**
 * Display rows for the Rewards page "old claims" panel. Pure: amounts come only from the breakdown (whose claimable
 * part the API cross-checked against the vault at one block) — never recomputed or reconciled here.
 */
export function oldClaims(bd: RewardsBreakdown | null, previouslyOwned: IdentitySummary[] = []): OldClaimsView {
  const fromBreakdown: FormerRunnerRow[] = (bd?.runners ?? [])
    .filter((r) => r.status !== 'held')
    .map((r) => {
      const last = r.periods[r.periods.length - 1];
      return {
        tokenId: r.tokenId,
        label: ENDED_LABEL[r.status as keyof typeof ENDED_LABEL] ?? r.status,
        heldFrom: r.periods[0]?.eligibleFrom ?? null,
        heldUntil: last?.until ?? null,
        epochs: r.epochs,
        earned: big(r.amount),
        unclaimed: big(r.unclaimed),
      };
    });
  const known = new Set(fromBreakdown.map((r) => r.tokenId));
  const fallback: FormerRunnerRow[] = previouslyOwned
    .filter((t) => !known.has(t.tokenId))
    .map((t) => ({ tokenId: t.tokenId, label: t.status === 'live' ? 'sold / transferred' : t.status === 'redeemed' ? 'redeemed (burned)' : 'converted back', heldFrom: null, heldUntil: null, epochs: [], earned: null, unclaimed: null }));
  const part = (e: RewardsBreakdown['epochs'][number], s: 'direct' | 'deferred' | 'run') => {
    const x = e.streams.find((y) => y.stream === s);
    return x ? { amount: big(x.amount), status: x.status } : null;
  };
  const epochs = (bd?.epochs ?? []).map((e) => ({
    epoch: e.epoch,
    weight: big(e.weight),
    identityWeight: big(e.identityWeight),
    runnerIds: e.runners.map((r) => r.tokenId),
    direct: part(e, 'direct'),
    deferred: part(e, 'deferred'),
    run: part(e, 'run'),
    amount: big(e.amount),
    unclaimed: big(e.unclaimed),
  }));
  return {
    formerRunners: [...fromBreakdown, ...fallback].sort((a, b) => Number(BigInt(a.tokenId) - BigInt(b.tokenId))),
    epochs,
    epochCount: bd?.epochCount ?? 0,
    complete: bd ? bd.complete : null,
    reason: bd?.reason ?? null,
    unclaimed: epochs.reduce((a, e) => a + e.unclaimed, 0n),
  };
}

// ------------------------------------------------------------------------------------------------
// Waterfall: recompute every settled epoch with the reference split and its own policy
// ------------------------------------------------------------------------------------------------

export function policyBig(p: Pick<PolicyView, 'holdersBps' | 'forgeBps' | 'roadBps' | 'prizeBps' | 'opsBps' | 'holderDeferredBps'>): WaterfallPolicy {
  return {
    holdersBps: BigInt(p.holdersBps),
    forgeBps: BigInt(p.forgeBps),
    roadBps: BigInt(p.roadBps),
    prizeBps: BigInt(p.prizeBps),
    opsBps: BigInt(p.opsBps),
    holderDeferredBps: BigInt(p.holderDeferredBps),
  };
}

export type WaterfallEpochRow = TreasurySnapshot['waterfall']['epochs'][number];

/** Accrual epoch status (GET /v1/treasury waterfall.epochs[].status), as shown on the Treasury and Verify pages. */
export const EPOCH_STATUS_LABEL: Record<WaterfallEpochRow['status'], string> = {
  open: 'open — accruing',
  'closed-unsettled': 'closed — awaiting settlement',
  settled: 'settled',
};

/** Only settled epochs have a split (open and closed-but-unsettled epochs are split once, at settlement). */
export function settledEpochs(rows: WaterfallEpochRow[]): WaterfallEpochRow[] {
  return rows.filter((r) => r.status === 'settled');
}

export interface WaterfallCheck {
  epoch: number;
  policyId: number;
  accrued: bigint;
  /** Recomputed with WaterfallLib's TypeScript mirror under the epoch's own policy. */
  matches: boolean;
  /** The five buckets (holders direct + deferred, Forge, road, prize, operations) sum exactly to R. */
  sumsToR: boolean;
  policyKnown: boolean;
}

/** Recompute every SETTLED epoch (open / unsettled rows have no split yet and are skipped). */
export function verifyWaterfall(rows: WaterfallEpochRow[], policies: PolicyView[]): WaterfallCheck[] {
  const byId = new Map(policies.map((p) => [p.policyId, p]));
  return settledEpochs(rows).map((r) => {
    const accrued = big(r.accrued);
    const p = byId.get(r.policyId);
    const parts = [r.holdersDirect, r.holdersDeferred, r.forge, r.road, r.prize, r.operations].map(big);
    const sum = parts.reduce((a, b) => a + b, 0n);
    let matches = false;
    if (p) {
      const s = waterfallSplit(accrued, policyBig(p));
      matches =
        s.holdersDirect === parts[0] && s.holdersDeferred === parts[1] && s.forge === parts[2] && s.road === parts[3] && s.prize === parts[4] && s.operations === parts[5];
    }
    return { epoch: r.epoch, policyId: r.policyId, accrued, matches, sumsToR: sum === accrued, policyKnown: !!p };
  });
}

/** The five-way totals as display rows (holders direct + deferred are one entitlement split in two parts). */
export function waterfallRows(t: TreasurySnapshot['waterfall']['totals']): { key: string; label: string; amount: bigint; note: string }[] {
  return [
    { key: 'holdersDirect', label: 'Holders — direct part', amount: big(t.holdersDirect), note: 'claimable once the epoch is final' },
    { key: 'holdersDeferred', label: 'Holders — deferred part', amount: big(t.holdersDeferred), note: 'same entitlement, released by Water/finish or the deadline' },
    { key: 'forge', label: 'Base Forge reserve', amount: big(t.forge), note: 'capped buybacks; bought RUNNER is burned' },
    { key: 'road', label: 'Road reserve', amount: big(t.road), note: 'per-run budgets — the only money a run decides' },
    { key: 'prize', label: 'Golden Shoe / prize reserve', amount: big(t.prize), note: 'escrowed when a run starts' },
    { key: 'operations', label: 'Operations and execution', amount: big(t.operations), note: 'per-run execution provision; capped spending' },
  ];
}

// ------------------------------------------------------------------------------------------------
// Hypothetical run of the road budget (mirror of apps/api simulate.ts, used in DEMO mode only)
// ------------------------------------------------------------------------------------------------

export interface HypotheticalRun {
  waterHits: number;
  overheatSteps: number;
  exit: 'harvest' | 'forge' | 'neutral' | 'none';
  waterSliceBps: bigint;
  overheatSliceBps: bigint;
  overheatCapBps: bigint;
}

export function simulateRoad(budget: bigint, r: HypotheticalRun) {
  let unassigned = budget;
  const take = (want: bigint) => {
    const got = want < unassigned ? want : unassigned;
    unassigned -= got;
    return got;
  };
  const cap = (budget * r.overheatCapBps) / 10_000n;
  let overheat = 0n;
  for (let k = 0; k < r.overheatSteps; k++) {
    const slice = (budget * r.overheatSliceBps) / 10_000n;
    const capLeft = cap - overheat;
    overheat += take(slice < capLeft ? slice : capLeft);
    if (overheat >= cap) break;
  }
  let water = 0n;
  for (let k = 0; k < r.waterHits; k++) water += take((budget * r.waterSliceBps) / 10_000n);
  const left = unassigned;
  const toForge = r.exit === 'forge' ? left : r.exit === 'harvest' || r.exit === 'none' ? 0n : left / 2n;
  const toHolders = r.exit === 'none' ? 0n : left - toForge;
  take(toHolders);
  take(toForge);
  return { budget, water, overheat, exitToHolders: toHolders, exitToForge: toForge, returnedToRoad: unassigned };
}

/** Five-bucket split of one epoch's revenue plus a hypothetical run of its whole road bucket (WaterfallLib + RunnerGame). */
export function previewWaterfall(revenue: bigint, policy: WaterfallPolicy, run: HypotheticalRun | null) {
  const split = waterfallSplit(revenue, policy);
  let holders = split.holdersDirect + split.holdersDeferred;
  let forge = split.forge;
  let roadUnassigned = split.road;
  let roadRun: ReturnType<typeof simulateRoad> | null = null;
  if (run) {
    roadRun = simulateRoad(split.road, run);
    holders += roadRun.water + roadRun.exitToHolders;
    forge += roadRun.overheat + roadRun.exitToForge;
    roadUnassigned = roadRun.returnedToRoad;
  }
  return { split, run: roadRun, outcome: { holders, forge, prize: split.prize, operations: split.operations, roadUnassigned } };
}

// ------------------------------------------------------------------------------------------------
// Runner identities: metadata and the base renderer
// ------------------------------------------------------------------------------------------------

export interface TokenMetadata {
  name: string;
  description: string;
  image: string;
  attributes: { trait_type: string; value: string }[];
}

function b64decode(s: string): string {
  const bin = atob(s);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Decode RunnerIdentity.tokenURI (data:application/json;base64,…). Returns null for anything else. */
export function decodeTokenUri(uri: string | null | undefined): TokenMetadata | null {
  if (!uri) return null;
  const m = /^data:application\/json;base64,(.+)$/.exec(uri);
  if (!m) return null;
  try {
    const j = JSON.parse(b64decode(m[1])) as TokenMetadata;
    return typeof j.image === 'string' ? j : null;
  } catch {
    return null;
  }
}

/** The SVG inside a data:image/svg+xml;base64 image, decoded. */
export function decodeSvgImage(image: string): string | null {
  const m = /^data:image\/svg\+xml;base64,(.+)$/.exec(image);
  if (!m) return null;
  try {
    return b64decode(m[1]);
  } catch {
    return null;
  }
}

const INK = '#0B0B0B';
const CREAM = '#FFF2D8';
const ORANGE = '#EC673A';

/** Brand head path (brand/source/rig/runner-rig.js HEAD): three swept tufts, no mouth, shared by every Runner. */
const HEAD = 'M36 58C19 58 14 52 20 46C25 42 40 44 43 41C22 35 18 27 24 22C32 15 50 28 58 28C42 8 49 2 57 7C74 15 84 30 88 37C106 41 117 56 116 77C116 103 96 118 73 118C45 118 30 99 30 80C30 70 32 64 36 58Z';
/** Eye shapes from the brand avatars: neutral, focused, surprised, mischievous, happy. */
const EYES = [
  `<ellipse cx="65" cy="80" rx="12" ry="18" fill="${CREAM}"/><ellipse cx="69" cy="81" rx="4.6" ry="8.5" fill="${INK}"/><ellipse cx="95" cy="77" rx="12" ry="18" fill="${CREAM}"/><ellipse cx="99" cy="78" rx="4.6" ry="8.5" fill="${INK}"/>`,
  `<path d="M53 64L79 68C79 94 59 100 54 83Z" fill="${CREAM}"/><path d="M86 68L109 61L111 80C106 96 88 94 86 68Z" fill="${CREAM}"/><ellipse cx="70" cy="79" rx="5" ry="8" fill="${INK}"/><ellipse cx="103" cy="78" rx="4.5" ry="8" fill="${INK}"/>`,
  `<ellipse cx="65" cy="79" rx="14" ry="20" fill="${CREAM}"/><ellipse cx="67" cy="80" rx="5.3" ry="9.4" fill="${INK}"/><ellipse cx="96" cy="77" rx="13" ry="19" fill="${CREAM}"/><ellipse cx="98" cy="78" rx="4.9" ry="8.9" fill="${INK}"/>`,
  `<ellipse cx="65" cy="82" rx="12" ry="13" fill="${CREAM}"/><ellipse cx="69" cy="83" rx="4.6" ry="6.1" fill="${INK}"/><ellipse cx="95" cy="77" rx="12" ry="18" fill="${CREAM}"/><ellipse cx="99" cy="78" rx="4.6" ry="8.5" fill="${INK}"/>`,
  `<path d="M54 82C55 65 75 62 78 78C69 72 63 74 54 82Z" fill="${CREAM}"/><path d="M85 78C87 62 105 61 109 77C99 70 94 71 85 78Z" fill="${CREAM}"/>`,
];
const ACCENTS = [ORANGE, '#76B5BF', '#73856A', '#EAAF43'];
const HEADBANDS = ['', ORANGE, CREAM, '#EAAF43'];
const FIELDS = [CREAM, '#F5E9D4', '#D6C4A6', '#D9E1D1'];
const SINGLETS = [CREAM, '#D6C4A6', CREAM, '#D6C4A6'];

function identityFrame(kind: number, accent: string): string {
  if (kind === 0) {
    return `<circle cx="256" cy="244" r="182" fill="none" stroke="${accent}" stroke-width="32"/><circle cx="256" cy="244" r="202" fill="none" stroke="${INK}" stroke-width="2" stroke-dasharray="2 13"/>`;
  }
  if (kind === 1) {
    let rays = '';
    for (let i = 0; i < 12; i++) rays += `<path d="M256 34V64" stroke="${accent}" stroke-width="11" transform="rotate(${i * 30} 256 244)"/>`;
    return rays;
  }
  if (kind === 2) {
    return `<g fill="none" stroke="${accent}" stroke-width="12"><path d="M54 430V231C54 56 458 56 458 231V430"/><path d="M81 430V231C81 56 431 56 431 231V430"/><path d="M108 430V231C108 56 404 56 404 231V430"/></g>`;
  }
  return '';
}

/**
 * Byte-for-byte TypeScript mirror of RunnerIdentityRenderer.svg(tokenId) (the immutable base renderer): the brand's
 * collectible bust (brand/source/collectibles) with the canonical head. Base traits come only from the
 * deployment-committed seed and the id. Used to show demo identities and to verify the on-chain image in the browser.
 */
export function identitySvg(seed: Hex, tokenId: bigint): string {
  const t = baseTraits(seed, tokenId);
  const accent = ACCENTS[t[3]];
  const singlet = SINGLETS[t[5]];
  const id = tokenId.toString();
  const bibSize = id.length <= 2 ? '29' : id.length === 3 ? '24' : '18';
  const body =
    `<path d="M74 462C70 407 88 365 139 344L190 330L316 330L369 347C415 365 441 405 438 463Z" fill="${INK}" stroke="${INK}" stroke-width="8"/>` +
    `<path d="M182 333L197 323L313 325L329 336L344 462H162L166 375Z" fill="${singlet}" stroke="${INK}" stroke-width="8" stroke-linejoin="round"/>` +
    `<path d="M196 325Q251 377 314 328" fill="none" stroke="${INK}" stroke-width="9"/>` +
    `<path d="M173 403L336 394L340 420L169 431Z" fill="${accent}"/>` +
    (t[5] >= 2 ? `<path d="M179 363L177 391M325 361L329 383" fill="none" stroke="${accent}" stroke-width="4" stroke-linecap="round"/>` : '') +
    `<rect x="216" y="377" width="79" height="56" rx="5" fill="${CREAM}" stroke="${INK}" stroke-width="5"/>` +
    `<text x="255" y="416" text-anchor="middle" font-family="Arial,sans-serif" font-size="${bibSize}" font-weight="900" fill="${INK}">${id}</text>`;
  const band = t[2] === 0 ? '' : `<path d="M32 59C53 48 86 43 109 53L114 65C80 57 53 63 31 72Z" fill="${HEADBANDS[t[2]]}" stroke="${INK}" stroke-width="2"/><path d="M32 61L18 72L28 76L38 65Z" fill="${HEADBANDS[t[2]]}" stroke="${INK}" stroke-width="2"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><rect width="512" height="512" fill="${FIELDS[t[4]]}"/>` +
    identityFrame(t[0], accent) +
    `<path d="M20 463H492" stroke="${INK}" stroke-width="3"/>` +
    body +
    `<g transform="translate(90 51) scale(2.5)"><path d="${HEAD}" fill="${INK}"/>${EYES[t[1]]}${band}</g>` +
    `<text x="24" y="493" font-family="Arial,sans-serif" font-size="14" font-weight="800" letter-spacing="1" fill="${INK}">RUNNER #${id}</text></svg>`
  );
}

function b64encode(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

export function svgDataUri(svg: string): string {
  return `data:image/svg+xml;base64,${b64encode(svg)}`;
}

export function jsonDataUri(obj: unknown): string {
  return `data:application/json;base64,${b64encode(JSON.stringify(obj))}`;
}

// ------------------------------------------------------------------------------------------------
// Collection manifest validation. The rules live in @runner/schema (validateCollectionManifest) and are the SAME code
// the API runs for POST /v1/operator/manifest/validate; this is the client-side fallback (demo mode, or before an
// operator session exists). Prototype/fixture manifests can never be published.
// ------------------------------------------------------------------------------------------------

/** Manifest input as typed by an operator: any JSON object (validation reports on every field). */
export type CollectionManifest = Record<string, unknown>;
export type { ManifestCheck } from '@runner/schema';
export type ManifestResult = ManifestValidationResult;

export function validateManifest(m: unknown, fixtureSeed: string): ManifestResult {
  return validateCollectionManifest(m, { fixtureSeed });
}

/** The labelled development fixture (contracts/script/DefaultConfig.sol; deployments/production.params.example.json). */
export function fixtureManifest(seed: string): CollectionManifest {
  return fixtureCollectionManifest(seed);
}

// ------------------------------------------------------------------------------------------------
// Delayed release scheduling preview (no transaction)
// ------------------------------------------------------------------------------------------------

export interface ReleasePreview {
  eta: number;
  delaySeconds: number;
  /** First accrual epoch a waterfall policy scheduled now could take effect from (strictly future and after the delay). */
  earliestPolicyEpoch: number;
  etaEpoch: number;
}

export function releasePreview(now: number, delay: number, genesis: number, epochSeconds: number, currentEpoch: number): ReleasePreview {
  const eta = now + delay;
  const etaEpoch = eta < genesis ? 0 : Math.floor((eta - genesis) / epochSeconds);
  return { eta, delaySeconds: delay, etaEpoch, earliestPolicyEpoch: Math.max(currentEpoch + 1, etaEpoch + 1) };
}

/** Is `phase` one where a finished run's result card belongs on screen? */
export function showsResult(phase: Phase, run: RunView | null): boolean {
  return !!run && run.status === 'finished' && !run.abandoned && (phase === 'finished' || phase === 'replay' || phase === 'warm-up');
}

// ------------------------------------------------------------------------------------------------
// Economic profile (LOCAL/TEST only; never approved for production)
// ------------------------------------------------------------------------------------------------

/** The label shown on Token & Treasury and Verify: "Economic profile: <label> — LOCAL/TEST only, not approved for
 *  production" (the launch canary: "CANARY only …, not audited, not approved for production"). The caveat is always
 *  present, even if a response's note were empty. */
export function economicProfileLine(p: EconomicProfileView | null, mode: SourceMode): string {
  if (!p) return 'Economic profile: not reported (yet) — profiles are LOCAL/TEST only; none is approved for production';
  const scoped = /LOCAL\/TEST/.test(p.note) || (p.id === 'LAUNCH_CANARY_V1' && /CANARY only/.test(p.note))
    || (p.id.startsWith('MAINNET_') && /MAINNET launch profile chosen by the owner/.test(p.note) && /not audited/.test(p.note));
  const caveat = scoped && /not approved for production/.test(p.note) ? p.note : `LOCAL/TEST only, not approved for production. ${p.note}`.trim();
  return `Economic profile: ${p.label} — ${caveat}${mode === 'demo' ? ' (DEMO simulates this profile in the browser)' : ''}`;
}
